import { ArgumentError } from '../utils/errors';

import { BaseAPIModel } from '@weezevent/weezjs-core';

const NB_DIGIT_BY_LEVEL = 4;
const NB_SUB_LEVEL = 3;

/*
    Recursive method to compute group display order.
    // we support only 2 sub levels
    // We store each level on 3 digits.
    // display order = [XXXX][YYYY][ZZZZ]
    // XXXX = display order of level 1.
    // YYYY = display order of level 2. or 0000
    // ZZZZ = display order of level 3. or 0000
*/
export function computeGroupDisplayOrder(group, groupsById) {
    if (group.groupDisplayOrder !== null) {
        // group display order is known, stop.
        return;
    }

    const normalizedDisplayOrder = (
        Array(NB_DIGIT_BY_LEVEL)
            .fill('0')
            .join('') + group.display_order
    ).slice(-NB_DIGIT_BY_LEVEL);
    if (group.parent === null || groupsById[group.parent] === undefined) {
        group.parentLevel = 0;
        group.normalizedDisplayOrder = normalizedDisplayOrder;
        return;
    }

    let parentGroup = groupsById[group.parent];
    computeGroupDisplayOrder(parentGroup, groupsById);
    group.parentLevel = parentGroup.parentLevel + 1;
    group.normalizedDisplayOrder = '' + parentGroup.normalizedDisplayOrder + normalizedDisplayOrder;
    return;
}

export default class RateGroup extends BaseAPIModel {
    fields() {
        return {
            id: { readOnly: true, primaryKey: true }
        };
    }

    static listUrl({ organizationId, eventId }) {
        if (!organizationId) {
            throw new ArgumentError(`[Model][RateGroup][getAll] organizationId is mandatory`);
        }
        if (!eventId) {
            return `/ticket/organizations/${organizationId}/organization_rate_groups`;
        }
        return `/ticket/organizations/${organizationId}/events/${eventId}/rate_groups`;
    }

    // Get the select options object for this group
    toOption(custom) {
        return {
            text: this.name ?? this.group_template.name,
            value: this.id,
            ...custom?.(this)
        };
    }

    // Custom sort for rateGroup, we need to take into consideration parent.
    static sort(groups) {
        const groupsById = {};
        groups.forEach(group => {
            group.groupDisplayOrder = null;
            groupsById[group.pk] = group;
        });
        groups.forEach(group => {
            group.parentGroup = group.parent && groupsById[group.parent] ? groupsById[group.parent] : null;
            computeGroupDisplayOrder(group, groupsById);
            group.groupDisplayOrder = Number(group.normalizedDisplayOrder) * 10 ** ((NB_SUB_LEVEL - group.parentLevel) * NB_DIGIT_BY_LEVEL);
        });

        if (!groups.length || groups[0].groupDisplayOrder === undefined) {
            return groups;
        }
        groups.sort((el1, el2) => el1.groupDisplayOrder - el2.groupDisplayOrder);
        return;
    }

    /*
        When the groups are "sort", we have the parentGroup relation between objects
        This method allow to follow an attrName through parents.
        Example use case : attr "showRates" is used to hide rates in subcategory in rates manager page.
        if a parent group as "showRates" to False, then the sub group need to be hidden.
    */
    getAttributeFromParent(attrName, defaultValue) {
        if (this.parentGroup === null || this.parentGroup === undefined) {
            return defaultValue;
        }

        return this.parentGroup.getAttributeFromParent(attrName, this.parentGroup[attrName]);
    }
}
